import React, { FC } from 'react'
import { Redirect } from 'react-router-dom'
import { RouteProps, routes } from 'constants/routes'
import { useGetOrganizationsQuery } from 'gqlTypes'
import { DynamicLoadingPage } from 'containers/DynamicLoadingPage'
import GenericErrorView from 'views/GenericErrorView'
import NotFoundView from 'views/NotFoundView'
import { trackEvent } from 'modules/analytics'
import debounce from 'debounce'

// it is unlikely, however, still possible that `trackEvent` would be
// called multiple times due to rerenders. To mitigate this risk we simply
// debounce the function.
const trackEventDebounced = debounce(trackEvent, 1000, true)

/**
 * Redirects users from URLs with the following format:
 *    /team/{teamShortId}...
 * to their workspace versions:
 *    /workspace/{workspaceId}...
 *
 * The teamShortId => workspaceId transformation works because the id for
 * workspaces are the same than for teams.
 */

const TeamToWorkspaceIdRedirect: FC<RouteProps<'TEAM'>> = ({
  match,
  location: { search, state, pathname },
}) => {
  const { data, loading, error } = useGetOrganizationsQuery()

  if (loading) return <DynamicLoadingPage />

  if (error) {
    return <GenericErrorView error={error} />
  }

  const teamShortId = match.params.teamShortId
  const organizations = data?.me?.organizations ?? []
  const organization = organizations.find(org => org?.shortId === teamShortId)

  if (!organization) {
    return <NotFoundView />
  }

  const workspaceId = organization.identifier

  trackEventDebounced('Redirect /team/ to /workspace/', {
    template: pathname.replace(teamShortId, ':teamShortId'),
  })

  return (
    <Redirect
      to={{
        search,
        state,
        pathname: pathname.replace(
          `/team/${teamShortId}`,
          `/workspace/${workspaceId}`
        ),
      }}
      push={false}
    />
  )
}

const NON_ID_ROUTES = [
  routes.TEAM_INVITE.template(),
  routes.TEAM_LICENSE_REDEEM_CONFIRM.template(),
  routes.TEAM_CREATE.template(),
]

export const TeamToWorkspaceRedirect: FC<RouteProps<'TEAM'>> = props => {
  const {
    match,
    location: { search, state, pathname },
  } = props

  if (NON_ID_ROUTES.includes(match.url)) {
    trackEventDebounced('Redirect /team/ to /workspace/', {
      template: match.url,
    })

    return (
      <Redirect
        to={{
          search,
          state,
          pathname: pathname.replace(`/team/`, `/workspace/`),
        }}
        push={false}
      />
    )
  }

  return <TeamToWorkspaceIdRedirect {...props} />
}
